import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import translations from "../../translation"; // Import translations

const ActiveUsersTable = ({ language }) => {
  const [userData, setUserData] = useState({
    id: '',
    fullname: '',
    email: '',
    mobileno: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersData, setUsersData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const rowsPerPage = 10;

  const translate = (key) => translations[language]?.[key] || key;

  useEffect(() => {
    fetchUsersData();

    if (selectedUser) {
      setUserData({
        id: selectedUser.id || '',
        fullname: selectedUser.fullname || '',
        email: selectedUser.email || '',
        mobileno: selectedUser.mobileno || ''
      });
    }
  }, [selectedUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const fetchUsersData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/admin/users`, {
        method: "GET"
      });
      const data = await response.json();
      if (data.success) {
        setUsersData(data.data || []);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    Swal.fire({
      title: translate("confirmTitle"),
      text: translate("saveChangesText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translate("yesSaveChanges"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const formData = new FormData();
          formData.append("fullname", userData.fullname);
          formData.append("email", userData.email);
          formData.append("mobileno", userData.mobileno);
          formData.append("id", userData.id);
          const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/admin/users`, {
            method: "POST",
            body: formData,
          });

          const data = await response.json();
          if (data.success) {
            Swal.fire(translate("updated"), translate("userDetailsUpdated"), "success");
            fetchUsersData(); // Refresh data
            setModalShow(false); // Close modal after successful update
          } else {
            Swal.fire(translate("error"), data.message, "error");
          }
        } catch (error) {
          Swal.fire(translate("error"), translate("errorSaveChanges"), "error");
        }
      }
    });
  };

  const handleToggle = async (userId, currentRole) => {
    const newRole = currentRole === "Approver" ? "User" : "Approver";

    Swal.fire({
      title: translate("confirmTitle"),
      text: translate("changeRoleText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translate("yesChangeRole"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const formData = new FormData();
          formData.append("id", userId);
          formData.append("role", newRole);

          // Optimistically update the local state before the API call
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === userId ? { ...user, role: newRole } : user
            )
          );

          // Perform API call
          const response = await fetch(
            `${process.env.REACT_APP_CHECK_USER_API}/api/admin/user/update-role`,
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await response.json();

          if (!data.success) {
            // If the API call fails, revert the state back to original
            setUsers((prevUsers) =>
              prevUsers.map((user) =>
                user.id === userId ? { ...user, role: currentRole } : user
              )
            );
            Swal.fire(
              translate("error"),
              data.message || translate("errorChangeRole"),
              "error"
            );
          } else {
            Swal.fire(
              translate("updated"),
              translate("roleUpdated"),
              "success"
            );
            await fetchUsersData();
          }
        } catch (error) {
          console.error("Error updating role:", error);

          // Revert the state back if an error occurs
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === userId ? { ...user, role: currentRole } : user
            )
          );

          Swal.fire(translate("error"), translate("errorChangeRole"), "error");
        }
      }
    });
  };



  const handleDelete = async (userId) => {
    Swal.fire({
      title: translate("confirmTitle"),
      text: translate("deleteUserText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translate("yesDelete"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const formData = new FormData();
          formData.append("id", userId);
          const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/admin/users`, {
            method: "DELETE",
            body: formData,
          });

          const data = await response.json();
          if (data.success) {
            Swal.fire(translate("deleted"), translate("userDeleted"), "success");
            fetchUsersData(); // Refresh data
          } else {
            Swal.fire(translate("error"), data.message, "error");
          }
        } catch (error) {
          Swal.fire(translate("error"), translate("errorDeleteUser"), "error");
        }
      }
    });
  };
  const handleView = (user, editMode = false) => {
    setSelectedUser(user);
    setIsEditMode(editMode);
    setModalShow(true);
  };
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = usersData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(usersData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{translate('activeUsersTitle')}</h3>
        <div className="flex items-center space-x-3">
          <div className="relative shadow-md">
            <input
              type="text"
              className="form-input pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder={translate('searchPlaceholder')}
            />
            <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <button className="bg-black text-white px-4 py-2 rounded-lg shadow-md flex items-center">
            <i className="fas fa-filter mr-2" />
            {translate('filterButton')}
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="text-center py-6 text-gray-600">{translate('loading')}</div>
      ) : usersData.length === 0 ? (
        <div className="text-center py-6 text-gray-600">{translate('noActiveUsers')}</div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('mobileNumber')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((user, index) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.fullname}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.mobileno}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-start text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View Button */}
                        <button
                          onClick={() => handleView(user)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md"
                        >
                          {translate('viewButton')}
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => handleView(user, true)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                          {translate('editButton')}
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                        >
                          {translate('deleteButton')}
                        </button>

                        <div className="flex items-center">
                          {/* Toggle Switch */}
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={user.role === 'Approver'} // Toggle ON if user.role is 'Approver'
                              onChange={() => handleToggle(user.id, user.role)} // Trigger role toggle
                              className="sr-only peer"
                            />
                            <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-blue-600 transition-all duration-300">
                              <div
                                className="absolute w-4 h-4 bg-white rounded-full left-1 top-0.5 peer-checked:translate-x-5 transition-transform duration-300"
                              ></div>
                            </div>
                          </label>

                          {/* Role Label */}
                          <span
                            className={`ml-2 text-sm font-medium ${user.role === 'Approver' ? 'text-blue-600 font-bold' : 'text-gray-500'
                              }`}
                            style={{ width: '80px', textAlign: 'left' }} // Fixed width for alignment
                          >
                            {user.role === 'Approver' ? translate('approverRole') : translate('userRole')}
                          </span>
                        </div>



                      </div>
                    </td>




                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${currentPage === 1
                ? "bg-gray-300 text-gray-500"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              {translate("prevButton")}
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${currentPage === totalPages
                ? "bg-gray-300 text-gray-500"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              {translate("nextButton")}
            </button>
          </div>
        </div>
      )}
      <Modal size="xl" show={modalShow} onHide={() => setModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? translate('editUser') : translate('userDetails')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isEditMode ? (
            <form>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  {translate('name')}
                </label>
                <input
                  type="text"
                  name="fullname"
                  value={userData.fullname}
                  onChange={handleInputChange}
                  className="form-input mt-1 block w-full border py-2 rounded ps-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  {translate('email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={userData.email}
                  onChange={handleInputChange}
                  className="form-input mt-1 block w-full border py-2 rounded ps-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  {translate('mobileNumber')}
                </label>
                <input
                  type="text"
                  name="mobileno"
                  value={userData.mobileno}
                  onChange={handleInputChange}
                  className="form-input mt-1 block w-full border py-2 rounded ps-2"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  className="mr-2"
                  onClick={handleSaveChanges}
                >
                  {translate('saveChanges')}
                </Button>
                <Button variant="secondary" onClick={() => setModalShow(false)}>
                  {translate('cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>{translate('field')}</th>
                  <th>{translate('value')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{translate('name')}</td>
                  <td>{selectedUser?.fullname || translate('notAvailable')}</td>
                </tr>
                <tr>
                  <td>{translate('email')}</td>
                  <td>{selectedUser?.email || translate('notAvailable')}</td>
                </tr>
                <tr>
                  <td>{translate('mobileNumber')}</td>
                  <td>{selectedUser?.mobileno || translate('notAvailable')}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
      </Modal>
    </div>

  );
};

export default ActiveUsersTable;
